// The text profile `instruction-text/1` (§14.2): the agent's representation of a document. Every
// generated unit — a rule with its reason, a `### Label: Title` heading with its attribute line and
// children, an authored Markdown segment — is separated from the next by exactly one blank line; an
// authored segment is copied unchanged except that typed inline references read as
// `label (kind; target)`, annotations as their label, and parameters as their values. Metadata and
// comments are never instructional body. A resolved document (resolve.ts) adds its resolved
// references and its composition changes after the body. Exact vectors: conformance/text/.
import { canonicalJson } from "./jcs";
import { inlineTokens, type InlineToken } from "./inline";
import { KINDS, PRESENTATION_ATTRS, RULES, TYPED_REFERENCES, table } from "./registry";
import type { Attrs } from "./attributes";
import type { DirectiveNode, MarkdownNode, Node, Parameter } from "./types";

export interface TextOptions {
  /** Supplied parameter values; a declared default stands in for an absent one (§12). */
  params?: Record<string, unknown>;
  /** The document's declarations, from `read()`. */
  parameters?: Map<string, Parameter>;
  /** Which Markdown nodes are parsed prose (from `read()`); others are copied without normalizing. Without it, every
   *  Markdown node outside an example, a verbatim body or a kind this registry does not define is prose. */
  prose?: Set<MarkdownNode>;
  /** Local identities (from `read()`): a typed reference to a missing `#id` reads `[unresolved]`. */
  identities?: Map<string, { kind: string }>;
  /** For a resolved body: does the instance `instance` have the explicit id `id`? A typed reference to `#id`, or to
   *  `#alias/id` in an included or inherited instance, that names one it does not have reads `[unresolved]`. */
  known?: (instance: string, id: string) => boolean;
}

/** An object or resource record a resolver enriched a reference with (§14.1). */
export interface ReferenceRecord { key: string; kind: string; label: string; target: string; status: "resolved" | "unresolved"; data?: unknown; source?: string; sha256?: string }

/** What the profile serializes: an authored tree's body, or a resolved one's with its records and changes. */
export interface TextSource {
  body: Node[];
  objects?: ReferenceRecord[];
  resources?: ReferenceRecord[];
  provenance?: { operation?: string; target?: string; uri: string; reason?: string }[];
}

const RULE_LABEL: Record<string, string> = table({ must: "MUST", always: "MUST", never: "NEVER", should: "SHOULD", avoid: "SHOULD NOT", may: "MAY", guardrail: "GUARDRAIL" });
const HEADING_LABEL: Record<string, string> = table({ policy: "Policy", instruction: "Instruction", context: "Context" });
const UNIT_EXCLUDED = new Set(["id", "title", "class", "style"]);
const RULE_EXCLUDED = new Set(["id", "if", "because", "not", "class", "style"]);
const PATCHES = new Set(["replace", "extend", "remove"]);

export function toText(doc: TextSource, opts: TextOptions = {}): string {
  let text = units(doc.body, opts).join("\n\n");
  const records = [...(doc.objects ?? []), ...(doc.resources ?? [])].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  if (records.length) text += `\n\n### Resolved references\n\n${records.map((r) => fence(canonicalJson(r), "json")).join("\n\n")}`;
  const changes = (doc.provenance ?? []).filter((p) => p.operation && PATCHES.has(p.operation));
  if (changes.length) {
    text += `\n\n### Composition changes\n\n${changes.map((c) => `- ${c.operation} ${c.target} (origin: ${c.uri})${c.reason ? ` — ${c.reason}` : ""}`).join("\n")}`;
  }
  return text.replace(/\n+$/, "") + "\n";
}

function units(nodes: Node[], o: TextOptions): string[] {
  const out: string[] = [];
  for (const n of nodes) {
    const u = n.type === "markdown" ? markdownUnit(n, o) : directiveUnit(n, o);
    if (u) out.push(u);
  }
  return out;
}

function markdownUnit(n: MarkdownNode, o: TextOptions): string {
  const source = !o.prose || o.prose.has(n) ? normalize(n.source, o, n.instance) : n.source;
  return trimLf(source);
}

/** A body copied as written: an example's, a verbatim one, or a kind this registry does not define (its fallback). */
const literalBody = (n: DirectiveNode) => n.kind === "example" || n.attrs.verbatim === true || !KINDS[n.kind];
const rawChildren = (n: DirectiveNode) => trimLf((n.children ?? []).map((c) => (c.type === "markdown" ? c.source : "")).join(""));

function directiveUnit(n: DirectiveNode, o: TextOptions): string {
  const a = n.attrs;
  if (RULES.has(n.kind)) {
    let label = RULE_LABEL[n.kind];
    if (n.kind === "should" && a.not === true) label = "SHOULD NOT";
    if (typeof a.if === "string") label += ` (if ${interpolate(a.if, o)})`;
    const body = literalBody(n) ? rawChildren(n) : units(n.children ?? [], o).join("\n\n");
    let t = `${label}: ${body}`;
    if (typeof a.because === "string") t += `\nBECAUSE: ${interpolate(a.because, o)}`;
    const rest = attributeLine(a, RULE_EXCLUDED);
    return rest ? `${t}\n${rest}` : t;
  }
  const label = n.kind === "example" ? (a.avoid === true ? "Counterexample" : "Example") : HEADING_LABEL[n.kind] ?? n.kind;
  const title = typeof a.title === "string" && a.title ? a.title : typeof a.id === "string" ? a.id : n.kind;
  const parts = [`### ${label}: ${title}`];
  const excluded = new Set(UNIT_EXCLUDED);
  if (KINDS[n.kind]?.family === "layout") for (const k of PRESENTATION_ATTRS) excluded.add(k);
  if (n.kind === "example") excluded.add("avoid");
  const line = attributeLine(a, excluded);
  if (line) parts.push(line);
  if (n.value) parts.push(fence(n.value.source, n.value.format));
  else if (n.kind === "example") parts.push(fence(rawChildren(n), "markdown"));
  else {
    const children = literalBody(n) ? rawChildren(n) : units(n.children ?? [], o).join("\n\n");
    if (children) parts.push(children);
  }
  return parts.join("\n\n");
}

/** `Attributes: {…}` in RFC 8785 form, or "" when nothing meaningful is left. */
function attributeLine(a: Attrs, excluded: Set<string>): string {
  const kept = Object.fromEntries(Object.entries(a).filter(([k]) => !excluded.has(k)));
  return Object.keys(kept).length ? `Attributes: ${canonicalJson(kept)}` : "";
}

/** A fence around literal text: a backtick run longer than any inside it, at least three. */
export function fence(text: string, lang: string): string {
  const longest = Math.max(0, ...(text.match(/`+/g) ?? []).map((r) => r.length));
  const marker = "`".repeat(Math.max(3, longest + 1));
  return `${marker}${lang}\n${text}${text.endsWith("\n") ? "" : "\n"}${marker}`;
}

const trimLf = (s: string) => s.replace(/^\n+|\n+$/g, "");

// ---- prose: typed references, annotations and parameters ----

/** A Markdown source with its inline forms in their text representation (§14.2 table). */
export function normalize(source: string, o: TextOptions, instance = "root"): string {
  const tokens = inlineTokens(source);
  return rewrite(source, 0, source.length, tokens, o, instance);
}

function rewrite(source: string, from: number, to: number, tokens: readonly InlineToken[], o: TextOptions, instance: string): string {
  let out = "";
  let at = from;
  for (const t of tokens) {
    if (t.start < at || t.end > to) continue; // nested inside one already rewritten, or outside this range
    out += source.slice(at, t.start);
    out += tokenText(source, t, tokens, o, instance);
    at = t.end;
  }
  return out + source.slice(at, to);
}

function tokenText(source: string, t: InlineToken, tokens: readonly InlineToken[], o: TextOptions, instance: string): string {
  if (t.type === "param") return paramText(t.name, o) ?? source.slice(t.start, t.end);
  if (t.type !== "directive") return source.slice(t.start, t.end);
  const inner = tokens.filter((x) => x.start >= t.labelStart && x.end <= t.labelEnd && x !== t);
  const label = rewrite(source, t.labelStart, t.labelEnd, inner, o, instance);
  if (TYPED_REFERENCES.has(t.kind) || t.kind === "ref" || t.kind === "cite") {
    const target = refOf(t.attributes);
    if (target === null) return source.slice(t.start, t.end);
    return `${label} (${t.kind}; ${target})${unresolved(target, o, instance) ? " [unresolved]" : ""}`;
  }
  return label;
}

/** A local target (`#id`, or `#alias/id` in a composed instance) that the document does not have. */
function unresolved(target: string, o: TextOptions, instance: string): boolean {
  if (!target.startsWith("#")) return false;
  const path = target.slice(1).split("/");
  const id = path.pop()!;
  if (o.known) return !o.known(path.length ? `${instance}/${path.join("/")}` : instance, id);
  return !!o.identities && !path.length && !o.identities.has(id);
}

function refOf(attributes: string | null): string | null {
  if (!attributes) return null;
  const m = /(?:^|[ \t])ref=(?:"((?:[^"\\]|\\.)*)"|([^\s{}"]+))/.exec(attributes);
  if (!m) return null;
  if (m[1] === undefined) return m[2];
  // (a quoted value with an escape JSON doesn't read is no target: the object stays as written)
  try { return JSON.parse(`"${m[1]}"`) as string; } catch { return null; }
}

/** A parameter's value as text, or null to leave `${name}` as written (§12). */
function paramText(name: string, o: TextOptions): string | null {
  const supplied = o.params && Object.prototype.hasOwnProperty.call(o.params, name) ? o.params[name] : undefined;
  return valueText(supplied !== undefined ? supplied : o.parameters?.get(name)?.default);
}

/** How a value reads in text (§12): strings and list items with their Markdown punctuation escaped, booleans and
 *  numbers as JSON scalars, lists joined with ", "; null when there is no value. */
export function valueText(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) return value.map((v) => escapeValue(String(v))).join(", ");
  if (typeof value === "string") return escapeValue(value);
  return JSON.stringify(value);
}

/** Interpolation in a rule's condition or reason: the same values, as data. */
function interpolate(s: string, o: TextOptions): string {
  return s.replace(/\$\{([A-Za-z_][A-Za-z0-9_.-]*)\}/g, (m, name: string) => paramText(name, o) ?? m);
}

/** A value inserts data, never syntax: the punctuation that could open Markdown or Instruction forms is escaped. */
function escapeValue(v: string): string {
  return v.replace(/[\\`*_{}[\]<>|~$]/g, (c) => `\\${c}`);
}
